import { locale } from "../constants";
import { DatepickerTypeEnum } from "../enums";

function getWeeksInYear(year: number): number {
  // Последний день года
  const lastDayOfYear = new Date(year, 11, 31);

  // Проверяем день недели 31 декабря
  const lastDayOfWeek = lastDayOfYear.getDay();

  // Находим ближайший к 31 декабря четверг
  const lastThursdayOfYear =
    lastDayOfYear.getDate() - ((lastDayOfWeek + 3) % 7);

  // Получаем дату последнего четверга года
  const lastThursdayDate = new Date(year, 11, lastThursdayOfYear);

  // Дата первого четверга года (ISO 8601)
  const firstThursdayOfYear = new Date(year, 0, 4); // 4 января
  const firstDayOfWeek = firstThursdayOfYear.getDay();
  const firstThursday =
    firstThursdayOfYear.getDate() - ((firstDayOfWeek + 3) % 7);

  // Если год начинается в четверг, или 31 декабря — четверг, то в году 53 недели
  if (firstThursdayOfYear.getDay() === 4 || lastThursdayDate.getDay() === 4) {
    return 53;
  }

  // В остальных случаях — 52 недели
  return 52;
}

/**
 * * Модифицировать дату
 * @param date Дата
 * @returns
 */
export function modifyDate(date: Date) {
  const baseDate = new Date(date); // Сохраняем копию исходной даты

  return {
    /**
     * Добавляет указанное количество дней к дате.
     * @param days Количество дней для добавления.
     * @returns Новый объект Date с добавленными днями.
     */
    addDays(days: number): Date {
      const result = new Date(baseDate);
      result.setDate(result.getDate() + days);
      return result;
    },

    /**
     * Добавляет указанное количество часов к дате.
     * @param hours Количество часов для добавления.
     * @returns Новый объект Date с добавленными часами.
     */
    addHours(hours: number): Date {
      const result = new Date(baseDate);
      result.setHours(result.getHours() + hours);
      return result;
    },

    /**
     * Добавляет указанное количество минут к дате.
     * @param minutes Количество минут для добавления.
     * @returns Новый объект Date с добавленными минутами.
     */
    addMinutes(minutes: number): Date {
      const result = new Date(baseDate);
      result.setMinutes(result.getMinutes() + minutes);
      return result;
    },

    /**
     * Проверяет, является ли дата выходным днем (суббота или воскресенье).
     * @returns true, если день суббота или воскресенье.
     */
    isWeekend(): boolean {
      const day = baseDate.getDay();
      return day === 0 || day === 6; // 0 - воскресенье, 6 - суббота
    },
    /**
     * Проверяет, является ли дата текущим днем
     * @returns true, если текущая дата
     */
    isCurrent(): boolean {
      return this.compareDates(new Date(), "hour");
    },
    /**
     * Проверяет, является ли месяц текущим
     * @returns true, если текущий месяц
     */
    isCurrentMonth(): boolean {
      return (
        baseDate.getFullYear() === new Date().getFullYear() &&
        baseDate.getMonth() === new Date().getMonth()
      );
    },
    /**
     * Проверяет, является ли год текущим
     * @returns true, если текущий год
     */
    isCurrentYear(): boolean {
      return baseDate.getFullYear() === new Date().getFullYear();
    },
    /**
     * Возвращает полное название месяца на основе локали.
     * @returns Название месяца на русском языке.
     */
    getMonthName(): string {
      return locale.monthNames[baseDate.getMonth()];
    },

    /**
     * Возвращает сокращенное название дня недели на основе локали.
     * @returns Сокращенное название дня недели на русском языке.
     */
    getWeekDayShortName(): string {
      return locale.weekDayShortNames[baseDate.getDay()];
    },

    /**
     * Очищает время, удаляя компоненты времени вплоть до указанного уровня.
     * @param level Уровень очистки (millisecond, second, minute, hour).
     * @returns Новый объект Date с очищенным временем.
     */
    clearTime(
      level: "millisecond" | "second" | "minute" | "hour" = "hour"
    ): Date {
      const result = new Date(baseDate);

      const clearFunctions: {
        [key in "millisecond" | "second" | "minute" | "hour"]: () => void;
      } = {
        millisecond: () => result.setMilliseconds(0),
        second: () => {
          result.setSeconds(0);
          result.setMilliseconds(0);
        },
        minute: () => {
          result.setMinutes(0);
          result.setSeconds(0);
          result.setMilliseconds(0);
        },
        hour: () => {
          result.setHours(0);
          result.setMinutes(0);
          result.setSeconds(0);
          result.setMilliseconds(0);
        },
      };

      clearFunctions[level]();

      return result;
    },

    /**
     * Сравнивает две даты с возможностью указания уровня точности (миллисекунды, секунды, минуты, часы).
     * @param other Дата для сравнения.
     * @param level Уровень точности сравнения (millisecond, second, minute, hour).
     * @returns true, если даты равны до указанного уровня точности.
     */
    compareDates(
      other: Date,
      level: "millisecond" | "second" | "minute" | "hour" = "hour"
    ): boolean {
      const firstDate = modifyDate(new Date(baseDate)).clearTime(level);
      const secondDate = modifyDate(new Date(other)).clearTime(level);

      return firstDate.getTime() === secondDate.getTime();
    },
    /**
     * Получить дату понедельника
     * @returns
     */
    getMonday(): Date {
      const date = new Date(baseDate);
      const day = date.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Если воскресенье, смещаем на -6, иначе до понедельника
      const monday = new Date(date);
      monday.setDate(date.getDate() + diff);
      return monday;
    },
    /**
     * Получить номер недели по стандарту ISO 8601
     * @returns Номер недели
     */
    getWeekNumber(): number {
      const d = new Date(baseDate.getTime());

      // Преобразуем день недели так, чтобы понедельник = 0
      const dayNum = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dayNum + 3); // Перемещаем дату к четвергу

      // Получаем 4 января данного года
      const yearStart = new Date(d.getFullYear(), 0, 4);

      // Вычисляем номер недели относительно 4 января (ISO 8601)
      const weekNumber = Math.ceil(
        ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
      );

      // Если номер недели равен 0, это последняя неделя предыдущего года
      if (weekNumber === 0) {
        return getWeeksInYear(d.getFullYear() - 1);
      }

      // Если номер недели больше, чем количество недель в году, то это 1-я неделя следующего года
      const totalWeeksInYear = getWeeksInYear(d.getFullYear());
      if (weekNumber > totalWeeksInYear) {
        return 1; // Первая неделя следующего года
      }

      return weekNumber;
    },
    /**
     * Форматировать дату
     * @param formatString Строка форматирования
     * @returns
     */
    format(formatString: string): string {
      const date = new Date(baseDate);
      const padZero = (num: number) => (num < 10 ? "0" + num : num.toString());

      const day = padZero(date.getDate());
      const month = padZero(date.getMonth() + 1);
      const year = date.getFullYear();
      const hours = padZero(date.getHours());
      const minutes = padZero(date.getMinutes());

      return formatString
        .replace("DD", day)
        .replace("MMMM", locale.monthNames[date.getMonth()])
        .replace("MM", month)
        .replace("YYYY", year.toString())
        .replace("hh", hours)
        .replace("mm", minutes);
    },

    /**
     * Форматировать дату на основании типа инпута
     * @param type Тип инпута
     * @returns
     */
    formatByInputType(type: DatepickerTypeEnum) {
      switch (type) {
        case DatepickerTypeEnum.Date:
          return this.format("DD.MM.YYYY");
        case DatepickerTypeEnum.DateTime:
          return this.format("DD.MM.YYYY в hh:mm");
        case DatepickerTypeEnum.Year:
          return this.format("YYYY");
        case DatepickerTypeEnum.Month:
          return this.format("MMMM");
        case DatepickerTypeEnum.Time:
          return this.format("hh:mm");
        case DatepickerTypeEnum.Week:
          const weekNum = this.getWeekNumber();
          return `Неделя ${weekNum.toString().padStart(2, "0")}, ${
            baseDate.getMonth() === 0 && weekNum > 50
              ? baseDate.getFullYear() - 1
              : baseDate.getFullYear()
          }`;
      }
    },

    /**
     * Распарсить сктроку в дату по типу инпута
     * @param str Строка
     * @param type Тип инпута
     * @param _date Изначальная дата
     */
    parseStringByInputType(str: string, type: DatepickerTypeEnum) {
      let date = new Date(baseDate);
      const clearValue = str.replace(/\D/g, "");

      const substr = (start: number, end: number | "full") => {
        return parseInt(
          clearValue.substring(start, end === "full" ? clearValue.length : end)
        );
      };

      switch (type) {
        case DatepickerTypeEnum.Date:
          date.setFullYear(substr(4, "full"), substr(2, 4) - 1, substr(0, 2));
          break;
        case DatepickerTypeEnum.DateTime:
          date.setFullYear(substr(4, 8), substr(2, 4) - 1, substr(0, 2));
          date.setHours(substr(8, 10), substr(10, 12));
          break;
        case DatepickerTypeEnum.Year:
          date.setFullYear(substr(0, "full"));
          break;
        case DatepickerTypeEnum.Month:
          const findIndex = locale.monthNames.findIndex(
            (name) => name.toLocaleLowerCase() === str.toLocaleLowerCase()
          );
          if (findIndex !== -1) date.setMonth(findIndex);
          break;
        case DatepickerTypeEnum.Week:
          // Регулярное выражение для извлечения недели и года
          const weekYearMatch = str.match(/(\d{1,2})\D*(\d{4})/);
          if (weekYearMatch) {
            let week = parseInt(weekYearMatch[1], 10); // Номер недели
            const year = parseInt(weekYearMatch[2], 10); // Год

            // Проверяем, сколько недель в данном году (52 или 53)
            const weeksInYear = getWeeksInYear(year);

            // Проверяем, чтобы номер недели не превышал максимальное количество недель
            if (week > weeksInYear) {
              week = weeksInYear;
            }

            // Устанавливаем год
            date.setFullYear(year);

            // ISO неделя: считаем первую неделю года
            const januaryFourth = new Date(year, 0, 4); // 4 января
            const dayOfWeek =
              januaryFourth.getDay() === 0 ? 7 : januaryFourth.getDay(); // Воскресенье = 7

            // Смещаем дату к понедельнику первой недели
            const firstMonday = new Date(januaryFourth);
            firstMonday.setDate(januaryFourth.getDate() - (dayOfWeek - 1));

            // Коррекция первой недели, если 1 января приходится на конец предыдущей недели
            if (firstMonday.getFullYear() !== year) {
              firstMonday.setDate(firstMonday.getDate() + 7); // Смещаем на следующую неделю
            }

            // Вычисляем целевую дату по номеру недели
            const targetDate = new Date(
              firstMonday.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000
            );

            // Проверка, чтобы целевая дата все еще относилась к правильной неделе
            const actualWeek = Math.ceil(
              (targetDate.getTime() - firstMonday.getTime()) /
                (7 * 24 * 60 * 60 * 1000) +
                1
            );

            if (actualWeek !== week) {
              console.warn(
                `Warning: Calculated week (${actualWeek}) doesn't match the input week (${week}).`
              );
            }

            // Устанавливаем целевую дату в объект `date`
            date.setMonth(targetDate.getMonth(), targetDate.getDate());

            date = modifyDate(date).getMonday();
          }
          break;
        case DatepickerTypeEnum.Time:
          date.setHours(substr(0, 2), substr(2, 4));
          break;
      }

      return date;
    },
  };
}
